// src/pages/admin/AdminGifts.jsx
import { useState, useEffect, useRef } from "react";
import {
  Plus, Pencil, Trash2, X, Save, Search, ToggleLeft, ToggleRight,
  Upload, Package, Truck, ChevronDown,
} from "lucide-react";
import adminApi from "../../api/adminApi";
import { toast } from "react-toastify";
import { GIFT_CATEGORIES, getCategoryLabel } from "../../constants/giftCategories";

const EMPTY_FORM = {
  title: "", priceNgn: "", description: "", deliveryNote: "",
  category: "other_packages", isCustom: false,
  deliveryTimeLabel: "8-10 Days", deliversTo: "all_countries",
  isActive: true, sortOrder: 0,
};

const STATUS_OPTIONS = ["pending", "processing", "shipped", "delivered", "cancelled"];

export default function AdminGifts() {
  const [tab, setTab] = useState("gifts");

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Gifts</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Manage gift catalog and customer orders</p>
        </div>
      </div>

      <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden w-fit">
        <button
          onClick={() => setTab("gifts")}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition ${tab === "gifts" ? "bg-purple-600 text-white" : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300"}`}
        >
          <Package size={14} /> Gift Catalog
        </button>
        <button
          onClick={() => setTab("orders")}
          className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition ${tab === "orders" ? "bg-purple-600 text-white" : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300"}`}
        >
          <Truck size={14} /> Gift Orders
        </button>
      </div>

      {tab === "gifts" ? <GiftCatalogTab /> : <GiftOrdersTab />}
    </div>
  );
}

// ── Gift Catalog Tab ────────────────────────────────────────────────────────

function GiftCatalogTab() {
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const fileInputRef = useRef(null);

  const fetchGifts = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get("/gifts");
      if (res.data.success) setGifts(res.data.gifts);
    } catch {
      toast.error("Failed to load gifts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchGifts(); }, []);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.priceNgn) {
      toast.error("Title and price are required");
      return;
    }
    if (!editingId && !imageFile) {
      toast.error("Image is required for a new gift");
      return;
    }
    if (!form.deliveryTimeLabel.trim()) {
      toast.error("Delivery time label is required (e.g. '30-Mins', '8-10 Days')");
      return;
    }

    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("priceNgn", form.priceNgn);
      fd.append("description", form.description);
      fd.append("deliveryNote", form.deliveryNote);
      fd.append("category", form.category);
      fd.append("isCustom", form.isCustom);
      fd.append("deliveryTimeLabel", form.deliveryTimeLabel);
      fd.append("deliversTo", form.deliversTo);
      fd.append("isActive", form.isActive);
      fd.append("sortOrder", form.sortOrder);
      if (imageFile) fd.append("image", imageFile);

      if (editingId) {
        await adminApi.put(`/gifts/${editingId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Gift updated — it will now appear in the correct category");
      } else {
        await adminApi.post("/gifts", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success("Gift added");
      }

      setShowForm(false);
      resetForm();
      fetchGifts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save gift");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (gift) => {
    setForm({
      title: gift.title,
      priceNgn: gift.priceNgn,
      description: gift.description || "",
      deliveryNote: gift.deliveryNote || "",
      category: gift.category || "other_packages",
      isCustom: !!gift.isCustom,
      deliveryTimeLabel: gift.deliveryTimeLabel || "8-10 Days",
      deliversTo: gift.deliversTo || "all_countries",
      isActive: gift.isActive,
      sortOrder: gift.sortOrder || 0,
    });
    setImagePreview(gift.imageUrl);
    setImageFile(null);
    setEditingId(gift._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this gift? This cannot be undone.")) return;
    try {
      await adminApi.delete(`/gifts/${id}`);
      toast.success("Gift deleted");
      fetchGifts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
    }
  };

  const handleToggle = async (gift) => {
    try {
      const fd = new FormData();
      fd.append("isActive", !gift.isActive);
      await adminApi.put(`/gifts/${gift._id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(gift.isActive ? "Gift hidden from catalog" : "Gift shown in catalog");
      fetchGifts();
    } catch {
      toast.error("Toggle failed");
    }
  };

  const filtered = gifts.filter((g) => g.title.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search gifts..."
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
          />
        </div>
        <button
          onClick={() => { setShowForm(true); resetForm(); }}
          className="flex items-center gap-2 px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition"
        >
          <Plus size={16} /> Add Gift
        </button>
      </div>

      {showForm && (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-semibold text-gray-900 dark:text-white">{editingId ? "Edit Gift" : "Add New Gift"}</h2>
            <button onClick={() => { setShowForm(false); resetForm(); }} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
              <X size={18} className="text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Title *</label>
                <input
                  type="text" value={form.title} required
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Big Teddy Bear"
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Price (₦) *</label>
                <input
                  type="number" min="1" required value={form.priceNgn}
                  onChange={(e) => setForm({ ...form, priceNgn: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Category *</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                >
                  {GIFT_CATEGORIES.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">Changing this moves the gift to that section immediately.</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Delivery Time Label *</label>
                <input
                  type="text" required value={form.deliveryTimeLabel}
                  onChange={(e) => setForm({ ...form, deliveryTimeLabel: e.target.value })}
                  placeholder="e.g. 30-Mins, 1-3 Days, 8-10 Days"
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                />
                <p className="text-xs text-gray-400 mt-1">Shown as the badge on the top-left of the gift image.</p>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Delivers To *</label>
                <select
  value={form.deliversTo}
  onChange={(e) => setForm({ ...form, deliversTo: e.target.value })}
  className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
>
  <option value="all_countries">All Countries</option>
  <option value="united_states">United States Only</option>
  <option value="usa_canada_uk">USA - Canada - UK</option>
</select>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, isCustom: !form.isCustom })}
                  className="flex items-center gap-2"
                >
                  {form.isCustom
                    ? <ToggleRight size={26} className="text-purple-600" />
                    : <ToggleLeft size={26} className="text-gray-400" />}
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    Customizable gift {form.isCustom && "(shows CUSTOM badge + image upload at checkout)"}
                  </span>
                </button>
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Description (optional)</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={3}
                  placeholder="Shown in the popup detail view when a user taps this gift"
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white resize-none"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Delivery Note (optional)</label>
                <input
                  type="text" value={form.deliveryNote}
                  onChange={(e) => setForm({ ...form, deliveryNote: e.target.value })}
                  placeholder="e.g. Ships in a padded gift box"
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Image {!editingId && "*"}</label>
                <div className="flex items-center gap-4">
                  {imagePreview && (
                    <img src={imagePreview} alt="preview" className="w-20 h-20 rounded-lg object-cover border border-gray-200 dark:border-gray-700" />
                  )}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-4 py-2.5 border border-dashed border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-500 dark:text-gray-400 hover:border-purple-400 transition"
                  >
                    <Upload size={14} /> {imagePreview ? "Change image" : "Upload image"}
                  </button>
                  <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-1">
              <button type="button" onClick={() => { setShowForm(false); resetForm(); }}
                className="flex-1 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="flex-1 py-2.5 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg transition flex items-center justify-center gap-2">
                {saving ? "Saving..." : <><Save size={15} />{editingId ? "Save Changes" : "Add Gift"}</>}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {[...Array(8)].map((_, i) => <div key={i} className="h-56 bg-white dark:bg-gray-900 rounded-xl animate-pulse border border-gray-200 dark:border-gray-800" />)}
        </div>
      ) : (
        GIFT_CATEGORIES.map((cat) => (
          <GiftSection
            key={cat.key}
            categoryKey={cat.key}
            title={cat.label}
            gifts={filtered.filter((g) => (g.category || "other_packages") === cat.key)}
            onToggle={handleToggle}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ))
      )}
    </div>
  );
}

// ── Gift Section (grouped by category) ─────────────────────────────────────
function GiftSection({ title, gifts, onToggle, onEdit, onDelete }) {
  if (gifts.length === 0) return null;
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1 h-4 bg-purple-600 rounded-full" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h2>
        <span className="text-xs text-gray-400 dark:text-gray-500 ml-auto">{gifts.length} item{gifts.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
        {gifts.map((gift) => (
          <div key={gift._id} className={`bg-white dark:bg-gray-900 rounded-xl border overflow-hidden ${gift.isActive ? "border-gray-200 dark:border-gray-800" : "border-orange-200 dark:border-orange-900 opacity-60"}`}>
            <div className="relative">
              <img src={gift.imageUrl} alt={gift.title} className="w-full h-28 object-cover" />
              <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full bg-black/70 text-white text-[9px] font-medium">
                {gift.deliveryTimeLabel}
              </span>
              {gift.isCustom && (
                <span className="absolute top-1.5 right-1.5 px-1.5 py-0.5 rounded-full bg-white text-gray-900 text-[9px] font-bold">
                  CUSTOM
                </span>
              )}
            </div>
            <div className="p-3">
              <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">{gift.title}</p>
              <p className="text-sm font-bold text-purple-600 dark:text-purple-400 mt-0.5">₦{gift.priceNgn.toLocaleString()}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">
  {gift.deliversTo === "united_states"
    ? "US only"
    : gift.deliversTo === "usa_canada_uk"
    ? "USA, Canada, UK"
    : "All countries"}
</p>
              <div className="flex items-center gap-1 mt-2">
                <button onClick={() => onToggle(gift)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition" title={gift.isActive ? "Hide" : "Show"}>
                  {gift.isActive ? <ToggleRight size={15} className="text-green-500" /> : <ToggleLeft size={15} className="text-gray-400" />}
                </button>
                <button onClick={() => onEdit(gift)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition">
                  <Pencil size={14} className="text-gray-400 hover:text-blue-500" />
                </button>
                <button onClick={() => onDelete(gift._id)} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition ml-auto">
                  <Trash2 size={14} className="text-gray-400 hover:text-red-500" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Gift Orders Tab — unchanged from before ─────────────────────────────────
function GiftOrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [statusDraft, setStatusDraft] = useState({});
  const [trackingDraft, setTrackingDraft] = useState({});
  const [noteDraft, setNoteDraft] = useState({});
  const [savingId, setSavingId] = useState(null);

  const fetchOrders = async (searchTerm = "") => {
    setLoading(true);
    try {
      const params = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : "";
      const res = await adminApi.get(`/gift-orders${params}`);
      if (res.data.success) setOrders(res.data.orders);
    } catch {
      toast.error("Failed to load gift orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchOrders(); }, []);

  const toggleExpand = (order) => {
    if (expandedId === order._id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(order._id);
    setStatusDraft((p) => ({ ...p, [order._id]: order.status }));
    setTrackingDraft((p) => ({ ...p, [order._id]: order.trackingNumber || "" }));
    setNoteDraft((p) => ({ ...p, [order._id]: order.adminNote || "" }));
  };

  const saveOrder = async (order, notifyCustomer) => {
    setSavingId(order._id);
    try {
      await adminApi.patch(`/gift-orders/${order._id}`, {
        status: statusDraft[order._id],
        trackingNumber: trackingDraft[order._id],
        adminNote: noteDraft[order._id],
        notifyCustomer,
      });
      toast.success(notifyCustomer ? "Saved and customer notified by email" : "Saved");
      fetchOrders(search);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update order");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2 max-w-md">
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && fetchOrders(search)}
          placeholder="Search order ref, receiver, or gift..."
          className="flex-1 px-4 py-2.5 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
        />
        <button onClick={() => fetchOrders(search)} className="px-4 py-2.5 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition">
          Search
        </button>
      </div>

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-16 bg-white dark:bg-gray-900 rounded-xl animate-pulse border border-gray-200 dark:border-gray-800" />)}</div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">No gift orders yet.</div>
      ) : (
        <div className="space-y-2">
          {orders.map((order) => (
            <div key={order._id} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50" onClick={() => toggleExpand(order)}>
                <ChevronDown size={16} className={`text-gray-400 transition-transform ${expandedId === order._id ? "rotate-180" : ""}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{order.giftTitle}</p>
                  <p className="text-xs text-gray-400">
                    {order.orderRef} · {order.userId?.username} ({order.userId?.email}) · {new Date(order.createdAt).toLocaleDateString("en-NG")}
                  </p>
                </div>
                <span className="text-sm font-semibold text-purple-600 dark:text-purple-400">₦{order.giftPriceNgn.toLocaleString()}</span>
                <span className="text-xs px-2.5 py-1 rounded-full font-medium capitalize bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  {order.status}
                </span>
              </div>

              {expandedId === order._id && (
                <div className="border-t border-gray-100 dark:border-gray-800 p-4 space-y-4">
                  {order.items && order.items.length > 0 && (
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                        {order.items.length} gift{order.items.length > 1 ? "s" : ""} in this order
                        {order.orderType === "cart_multiple_addresses" && " · different recipients"}
                      </p>
                      <div className="space-y-2">
                        {order.items.map((it, idx) => (
                          <div key={idx} className="text-xs text-gray-700 dark:text-gray-300 border-b border-gray-100 dark:border-gray-800 last:border-0 pb-2 last:pb-0">
                            <p className="font-semibold text-gray-900 dark:text-white">
                              {it.giftTitle} {it.quantity > 1 && `× ${it.quantity}`} — ₦{(it.giftPriceNgn * it.quantity).toLocaleString()}
                            </p>
                            {order.orderType === "cart_multiple_addresses" && (
                              <p className="text-gray-500 dark:text-gray-400 mt-0.5">
                                → {it.receiverName}, {it.address}{it.apartmentNumber ? `, Apt ${it.apartmentNumber}` : ""}, {it.city}, {it.state} {it.zipCode}, {it.country} · {it.phoneNumber}
                                {it.note && ` · Note: ${it.note}`}
                              </p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-gray-600 dark:text-gray-400">
                    <p><strong className="text-gray-900 dark:text-white">Sender:</strong> {order.senderName}</p>
                    <p><strong className="text-gray-900 dark:text-white">Receiver:</strong> {order.receiverName}</p>
                    <p><strong className="text-gray-900 dark:text-white">Phone:</strong> {order.phoneNumber}</p>
                    <p><strong className="text-gray-900 dark:text-white">Country:</strong> {order.country}</p>
                    <p className="sm:col-span-2"><strong className="text-gray-900 dark:text-white">Address:</strong> {order.address}{order.apartmentNumber ? `, Apt ${order.apartmentNumber}` : ""}, {order.city}, {order.state} {order.zipCode}</p>
                    {order.note && <p className="sm:col-span-2"><strong className="text-gray-900 dark:text-white">Note:</strong> {order.note}</p>}
                    {order.referenceImageUrl && (
                      <p className="sm:col-span-2">
                        <strong className="text-gray-900 dark:text-white">Reference image:</strong>{" "}
                        <a href={order.referenceImageUrl} target="_blank" rel="noopener noreferrer" className="text-purple-600 dark:text-purple-400 hover:underline">View image</a>
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-gray-100 dark:border-gray-800">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Status</label>
                      <select
                        value={statusDraft[order._id] || order.status}
                        onChange={(e) => setStatusDraft((p) => ({ ...p, [order._id]: e.target.value }))}
                        className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white capitalize"
                      >
                        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Tracking Number</label>
                      <input
                        type="text"
                        value={trackingDraft[order._id] ?? order.trackingNumber ?? ""}
                        onChange={(e) => setTrackingDraft((p) => ({ ...p, [order._id]: e.target.value }))}
                        placeholder="e.g. DHL123456789"
                        className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Note for customer</label>
                      <input
                        type="text"
                        value={noteDraft[order._id] ?? order.adminNote ?? ""}
                        onChange={(e) => setNoteDraft((p) => ({ ...p, [order._id]: e.target.value }))}
                        placeholder="Optional message"
                        className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:text-white"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => saveOrder(order, false)}
                      disabled={savingId === order._id}
                      className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                    >
                      Save only
                    </button>
                    <button
                      onClick={() => saveOrder(order, true)}
                      disabled={savingId === order._id}
                      className="px-4 py-2 text-sm font-medium bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white rounded-lg transition"
                    >
                      {savingId === order._id ? "Saving..." : "Save & Email Customer"}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}